import Data from '../States/Data';
import Helper from './Helper';
import Pattern from './Pattern';

/*
 * Upper bound when a reading runs to the end of a chapter without us knowing
 * the verse count — the longest chapter in the Bible is Psalm 119 with 176
 * verses. Highlighting walks the verses that are actually rendered, so listing
 * numbers past the end of the chapter is harmless.
 */
const CHAPTER_VERSE_LIMIT = 200;

/* Expanded verse numbers, the shape connectVerses() in Chapter.svelte wants.
 * Empty when either end is missing, which is how a whole chapter says it has
 * nothing to highlight. */
function verseRange(first, last) {
	const verses = [];
	if (first !== null && last !== null)
		for (let number = first; number <= last; number++)
			verses.push(number);

	return verses;
}

export default class Scripture {
	static async bookCodeByName(name) {
		const mapping = await Data.absolute('mapping');
		for (const code in mapping.books) {
			const item = mapping.books[code];
			if (item.includes(name.toLowerCase()))
				return code;
		}
	}

	static async bookByCode(code) {
		let book = '';
		const version = await Data.relative('version');
		scan:
			for (const testament of version.testaments)
				for (const item of testament.books)
					if (item.code === code) {
						book = item;
						break scan;
					}

		return book;
	}

	static async bookNameByCode(code) {
		const book = await Scripture.bookByCode(code);
		return book.name;
	}

	static async reference(name) {
		const reference = {};
		const label = name.replace(/\s+vers\s+/i, ':');
		const parts = label.match(/([0-9]+\s*)*([^0-9]+)([0-9]+)([\:\-0-9\s]+)*/);
		if (parts) {
			const number = parts[1] || '';
			let book = `${number}${parts[2]}`.trim();
			book = await Scripture.bookCodeByName(book);
			if (book) {
				reference.book = book;
				reference.name = parts[0];
				reference.chapter = parts[3];
				if (parts[4]) {
					let range = parts[4].trim();
					range = range.replace(':', '').split('-');
					if (Helper.undefined(range[1])) range[1] = range[0];

					const verses = [];
					for (let number = parseInt(range[0]); number <= parseInt(range[1]); number++)
						verses.push(number);

					reference.verses = verses;
				}
			}
		}

		return reference;
	}

	/*
	 * Parses a reading plan passage code into the chapter to open and the verses
	 * to highlight. The importer writes these codes (see admin/prisma) in five
	 * deliberately unambiguous shapes:
	 *
	 *   PSA_1           whole chapter
	 *   GEN_1-2         chapter range
	 *   MAT_1:1-17      verse range inside one chapter
	 *   MAT_10:16-11:1  crosses a chapter border
	 *   2CH_5:2-6:end   crosses a border, closing chapter read to the end
	 *
	 * Returns the first chapter — that is where the reading starts, and the
	 * reader swipes on from there — plus `verses` as an expanded list of verse
	 * numbers, the shape connectVerses() in Chapter.svelte already expects. It
	 * stays empty when the reading starts at the top of a chapter, so nothing is
	 * highlighted. A reading that starts mid-chapter and runs past it highlights
	 * through to the end of that first chapter.
	 *
	 * `to`, `fromVerse` and `toVerse` describe the whole reading rather than just
	 * its first chapter, so passageCovers() and passageReference() below can tell
	 * which chapters still belong to it. `fromVerse` is null when the reading is
	 * whole chapters, `toVerse` when it runs to the end of its closing one.
	 */
	static parsePassage(code) {
		const parts = String(code).split('_');
		const book = parts[0];
		const label = parts[1] || '';
		const match = label.match(/^([0-9]+)(?::([0-9]+)-(?:([0-9]+)(?::([0-9]+|end))?))?(?:-([0-9]+))?$/);
		if (!book || !match) return null;

		const chapter = parseInt(match[1]);
		const from = match[2] ? parseInt(match[2]) : null;
		const tail = match[3] ? parseInt(match[3]) : null;
		const crosses = Helper.is(match[4]);

		/*
		 * Crossing a border spells both sides out, so there the tail is the closing
		 * chapter and match[4] its last verse; a range of whole chapters has no
		 * colon at all and lands in the trailing group instead.
		 */
		let to = chapter;
		if (crosses) to = tail;
		else if (match[5]) to = parseInt(match[5]);

		let toVerse = null;
		if (crosses) toVerse = match[4] === 'end' ? null : parseInt(match[4]);
		else if (from !== null) toVerse = tail;

		// Same chapter: the tail is the end verse. Otherwise read to the chapter end.
		const until = crosses ? CHAPTER_VERSE_LIMIT : tail;

		return {
			book,
			chapter,
			location: `${book}_${chapter}`,
			verses: verseRange(from, until),
			to,
			fromVerse: from,
			toVerse
		};
	}

	/* Whether a chapter still belongs to a reading — `GEN_1-3` covers three. */
	static passageCovers(code, location) {
		const passage = code ? Scripture.parsePassage(code) : null;
		if (!passage) return false;

		const parts = String(location).split('_');
		const chapter = parseInt(parts[1]);
		return parts[0] === passage.book && chapter >= passage.chapter && chapter <= passage.to;
	}

	/*
	 * The verses of one chapter of a reading, in the shape connectVerses() in
	 * Chapter.svelte highlights. A reading of whole chapters has nothing to point
	 * at and answers null, so the chapter is shown as it is; one that starts or
	 * stops mid-chapter marks from where it begins to where it ends, which for a
	 * chapter in between is all of it.
	 */
	static passageReference(code, location) {
		const passage = Scripture.passageCovers(code, location) ? Scripture.parsePassage(code) : null;
		if (!passage || passage.fromVerse === null) return null;

		const chapter = parseInt(String(location).split('_')[1]);
		const first = chapter === passage.chapter ? passage.fromVerse : 1;
		const last = chapter === passage.to && passage.toVerse !== null ? passage.toVerse : CHAPTER_VERSE_LIMIT;

		const verses = verseRange(first, last);
		return verses.length ? {book: passage.book, chapter, verses} : null;
	}

	static async infoByCode(code) {
		const parts = code.split('_');
		const book = await this.bookByCode(parts[0]);

		const info = {code};
		info.book = {
			code: parts[0] || '',
			uri: parts[0] ? book.uri : '',
			name: parts[0] ? book.name : ''
		};

		info.chapter = {
			code: parts[1] ? `${parts[0]}_${parts[1]}` : '',
			uri: parts[1] ? `${info.book.uri}/${parts[1]}` : '',
			name: parts[1] ? `${info.book.name} ${parts[1]}` : '',
			number: parts[1] || ''
		};

		info.verse = {
			code: parts[2] ? `${parts[0]}_${parts[1]}_${parts[2]}` : '',
			uri: parts[2] ? info.chapter.uri : '',
			name: parts[2] ? `${info.chapter.name}:${parts[2]}` : '',
			number: parts[2] || ''
		};

		return info;
	}

	static async sortVerses(verses) {
		const mapping = await Data.absolute('mapping');
		const book_keys = Object.keys(mapping.books);

		const list = {};
		for (const verse of verses) {
			const info = await this.infoByCode(verse);
			const book = book_keys.indexOf(info.book.code) + 1;
			const position = (book * 1000000) + (info.chapter.number * 1000) + parseInt(info.verse.number);
			list[verse] = position;
		}

		const sorted = Object.keys(list).sort((a, b) => list[a] - list[b]);
		return sorted;
	}

	static verseByChapter(chapter, verse) {
		let scripture = chapter;
		scripture = Pattern.replace(scripture, /<span\sdata-type="char"\sdata-style="sc">(.+?)<\/span>/ig, '$1');
		scripture = Pattern.replace(scripture, /<span\sdata-type="ref"[^\<]+?<\/span>/ig, ' ');
		scripture = Pattern.replace(scripture, /<span\sdata-type="char"[^\<]+?<\/span>/ig, ' ');
		scripture = Pattern.replace(scripture, /<span\sdata-type="note"[^\<]+?<\/span>/ig, ' ');
		scripture = Pattern.replace(scripture, /<i\sclass="fa[^\<]+?<\/i>/ig, ' ');
		scripture = Pattern.replace(scripture, /(<p>|<\/p>)/ig, ' ');
		scripture = Pattern.replace(scripture, /(<x\-v>|<\/x\-v>)/ig, '');
		scripture = Pattern.replace(scripture, /[ ]{2,}/ig, ' ');

		const pattern = `<a\\sdata-verse="${verse}">[0-9]+</a>(.+?)(?:<a|<h3|$)`;
		const expression = new RegExp(pattern, 'im');
		scripture = Pattern.get(scripture, expression);
		scripture = scripture.trim();
		return scripture;
	}

	static async maxChapter(code) {
		let max = 0;
		const book = await Data.relative(`books/${code}`);
		for (let chapter of book.chapters) {
			const number = parseInt(chapter.number);
			if (number > max)
				max = number;
		}

		return max;
	}

	static async changeChapter(code, previous) {
		const current = await Scripture.infoByCode(code);
		const max = await Scripture.maxChapter(current.book.code);

		let info = {};
		let change_number = parseInt(current.chapter.number);
		change_number = previous ? (change_number - 1) : (change_number + 1);
		if (previous ? (change_number < 1) : (change_number > max)) {
			let change_book = previous ? 'REV' : 'GEN';
			let previous_book = change_book;
			let capture_book = false;
			const mapping = await Data.absolute('mapping');
			for (let book in mapping.books) {
				if (capture_book) {
					change_book = book;
					break;
				}
				else if (previous && (book === current.book.code)) {
					change_book = previous_book;
					break;
				}
				else if (!previous && (book === current.book.code))
					capture_book = true;

				previous_book = book;
			}

			change_number = previous ? await Scripture.maxChapter(change_book) : 1;
			info = await Scripture.infoByCode(`${change_book}_${change_number}`);
		}
		else info = await Scripture.infoByCode(`${current.book.code}_${change_number}`);

		return info;
	}

	static async previousChapter(code) {
		return Scripture.changeChapter(code, true);
	}

	static async nextChapter(code) {
		return Scripture.changeChapter(code);
	}
}